import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../theme/context/ThemeContext';

const FALLBACK_NAME = '---';
const MAX_WIDTH = 840;
// Shimmer wide enough to clear the text, but not overpower it
const SHIMMER_WIDTH = 480;
const SIDE_PADDING = 32;
const TEXT_WRAPPER_PADDING = 24;
const TEXT_PADDING = 16;
const CONTAINER_PADDING = 16;

export const GreetingComponentV3 = ({ user }) => {
  const { darkMode } = useTheme();
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : MAX_WIDTH
  );
  const [windowHeight, setWindowHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 900
  );

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const greeting = useMemo(() => {
    const currentHour = new Date().getHours();
    if (currentHour >= 17) return 'Evening';
    if (currentHour >= 12) return 'Afternoon';
    return 'Morning';
  }, []);

  const firstName = useMemo(() => {
    const raw =
      user?.preferredName ||
      user?.preferred_name ||
      user?.displayName ||
      user?.display_name ||
      user?.nickname ||
      user?.firstName ||
      user?.first_name ||
      (typeof user?.name === 'string' ? user.name.split(' ')[0] : undefined);

    if (!raw) return FALLBACK_NAME;
    const cleaned = raw.trim();
    return cleaned.length > 0 ? cleaned : FALLBACK_NAME;
  }, [user]);

  const fullText = useMemo(() => `${greeting}, ${firstName}.`, [greeting, firstName]);
  const hasRealUserData = firstName !== FALLBACK_NAME;

  const viewportWidth = Math.min(windowWidth || 0, MAX_WIDTH);
  const isShort = windowHeight < 800;
  const isVeryShort = windowHeight < 700;
  const isTinyHeight = windowHeight < 600;
  const isNarrow = windowWidth < 900;
  const shouldScaleDown = windowWidth < 1200 || isShort;
  let responsiveBase;
  if (!shouldScaleDown) {
    // Preserve the original large-screen sizing
    responsiveBase = Math.min(viewportWidth * 0.065, 36);
    if (viewportWidth < 420) {
      responsiveBase += 2;
    }
    responsiveBase = Math.max(responsiveBase, 30);
  } else {
    // Slightly reduced sizing to more closely match mobile feel on web
    responsiveBase = Math.min(viewportWidth * 0.06, 34);
    if (viewportWidth < 420) {
      responsiveBase += 1;
    }
    if (windowWidth < 1200) {
      responsiveBase -= 1;
    }
    if (isNarrow) {
      responsiveBase -= 2;
    }
    if (isShort) {
      responsiveBase -= 2;
    }
    if (isVeryShort) {
      responsiveBase -= 3;
    }
    if (isTinyHeight) {
      responsiveBase -= 3;
    }
    responsiveBase = Math.max(responsiveBase, 24);
  }
  const charCount = Math.max(fullText.length, 1);
  const lengthScale = charCount > 18 ? 18 / charCount : 1;
  const greetingFontSize = Math.max(responsiveBase * lengthScale, shouldScaleDown ? 22 : 26);

  const isCompact = shouldScaleDown && (windowWidth < 420 || isVeryShort);
  const dynamicSidePadding = shouldScaleDown
    ? (isCompact ? 10 : isNarrow || isShort ? 20 : SIDE_PADDING)
    : SIDE_PADDING;
  const dynamicTextWrapperPadding = shouldScaleDown
    ? (isCompact ? 8 : isNarrow || isShort ? 16 : TEXT_WRAPPER_PADDING)
    : TEXT_WRAPPER_PADDING;
  const dynamicTextPadding = shouldScaleDown
    ? (isCompact ? 6 : isNarrow || isShort ? 12 : TEXT_PADDING)
    : TEXT_PADDING;
  const dynamicContainerPadding = shouldScaleDown
    ? (isCompact ? 8 : isNarrow || isShort ? 12 : CONTAINER_PADDING)
    : CONTAINER_PADDING;
  let dynamicMaxWidth = MAX_WIDTH;
  if (shouldScaleDown) {
    if (windowWidth < 1200) {
      dynamicMaxWidth = 780;
    }
    if (isShort) {
      dynamicMaxWidth = Math.min(dynamicMaxWidth, 760);
    }
    if (isVeryShort) {
      dynamicMaxWidth = Math.min(dynamicMaxWidth, 700);
    }
    if (windowWidth < 900) {
      dynamicMaxWidth = Math.min(dynamicMaxWidth, 720);
    }
    if (windowWidth < 720) {
      dynamicMaxWidth = Math.min(dynamicMaxWidth, 640);
    }
  }
  // Slightly darker in dark mode to reduce glow, keep light mode as-is
  const textColor = darkMode ? '#383838' : '#CCCCCC';

  // Theme-aware shimmer: darker sweep for both themes to reduce brightness
  const shimmerGradient = darkMode
    ? 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.42) 50%, rgba(255,255,255,0) 100%)'
    : 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.28) 50%, rgba(0,0,0,0) 100%)';

  const baseTextStyle = {
    color: textColor,
    fontSize: `${greetingFontSize}px`,
    lineHeight: '1.2', // Explicit line-height for consistent Safari rendering
    paddingLeft: dynamicTextPadding,
    paddingRight: dynamicTextPadding,
    // Force PPEditorial first to mirror mobile styling, fall back gracefully
    fontFamily: 'PPEditorialNew-UltralightItalic, "PPEditorial New", serif',
    fontWeight: 200, // Required for Safari - must match @font-face descriptor
    fontStyle: 'italic', // Required for Safari - must match @font-face descriptor
    textAlign: 'center',
    whiteSpace: 'nowrap',
    width: '100%',
    display: 'block',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    letterSpacing: '0.01em', // Slight letter spacing for consistent Safari rendering
    transform: 'translateZ(0)', // Force GPU rendering for consistent appearance
    backfaceVisibility: 'hidden', // Improve Safari rendering consistency
  };

  const shimmerTextStyle = {
    ...baseTextStyle,
    color: 'transparent',
    backgroundImage: shimmerGradient,
    backgroundSize: `${SHIMMER_WIDTH}px 100%`,
    backgroundRepeat: 'no-repeat',
    // Start further off-screen so the sweep completes cleanly
    backgroundPosition: `-${SHIMMER_WIDTH}px 0`,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    position: 'absolute',
    inset: 0,
    animation: 'greeting-v3-shimmer 2.6s linear infinite',
    pointerEvents: 'none',
  };

  return (
    <div className="w-full flex justify-center px-4" aria-label="Greeting">
      <style>{`
        @keyframes greeting-v3-shimmer {
          0% { background-position: -${SHIMMER_WIDTH}px 0; }
          100% { background-position: ${SHIMMER_WIDTH}px 0; }
        }
      `}</style>
      <div
        className="relative w-full max-w-[840px]"
        style={{
          borderRadius: 999,
          paddingLeft: dynamicContainerPadding,
          paddingRight: dynamicContainerPadding,
          maxWidth: dynamicMaxWidth,
        }}
      >
        <div
          className="flex flex-row items-center justify-center w-full py-2"
          style={{ paddingLeft: dynamicSidePadding, paddingRight: dynamicSidePadding }}
        >
          <div
            className="flex w-full justify-center"
            style={{ paddingLeft: dynamicTextWrapperPadding, paddingRight: dynamicTextWrapperPadding }}
          >
            <div className="relative inline-block w-full text-center overflow-hidden">
              <span style={baseTextStyle}>{hasRealUserData ? fullText : fullText}</span>
              <span aria-hidden style={shimmerTextStyle}>{hasRealUserData ? fullText : fullText}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

